// eslint-disable-next-line import/no-cycle
import type { Fields } from "@vuex-orm/core";
import { Action } from "./ActionHierarchy";

export class Overlay extends Action {
  // This is the name used as module name of the Vuex Store.
  public static entity = "overlays";

  public static baseEntity = "actions";

  public readonly isBrowserAction = false;

  public static fields(): Fields {
    return {
      ...super.fields(),
      text: this.string(""),
    };
  }

  public static get humanName(): string {
    return "Text Overlay";
  }

  public declare text: string;

  public get estimatedReadingTime(): number {
    // we want to be pretty conservative here.
    const wordCount = (this.text || "").split(/\s+/).length;
    const wordsPerSecond = 3;
    return 2 + wordCount / wordsPerSecond;
  }

  public get nextOverlay(): Overlay | null {
    if (this.index === null) return null;

    return Overlay.query()
      .where("videoId", this.videoId)
      .where("index", (index) => index > (this.index || 0))
      .where("visible", true)
      .first();
  }

  // How to find the actions this overlay should cover:
  // 1. Estimate the reading time of the overlay.
  // 2. Fill that time with visible actions (including pauses), stop filling when we encounter
  //    another overlay.
  public get actions(): Action[] {
    const possibleActions = this.nextActionsUntilNextOverlay;
    let elapsed = 0.0;
    const actions: Action[] = [];
    possibleActions.some((action) => {
      if (action.visible) {
        elapsed += action.timings.total || 0;
      }
      actions.push(action);
      // Returning true breaks the some() loop:
      return elapsed > this.estimatedReadingTime;
    });
    return actions;
  }

  public get nextActionsUntilNextOverlay(): Action[] {
    if (this.index === null) return [];

    return Action.query()
      .where("videoId", this.videoId)
      .where(
        "index",
        (index) =>
          index > (this.index || 0) && (!this.nextOverlay || index < (this.nextOverlay.index || 0)),
      )
      .where("visible", true)
      .all();
  }
}
